/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2022, Zumper
 */

// https://chakra-ui.com/docs/styled-system/theming/component-style#styling-single-part-components

import type { ComponentStyleConfig } from '@chakra-ui/theme'
import { SystemStyleFunction, mode } from '@chakra-ui/theme-tools'

const normalizeSpacing = {
  padding: 0,
  margin: 0,
}

const variantDisplay48: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: '-0.576px',
  fontFamily: 'heading',
  fontSize: '5xl',
  fontWeight: 'extrabold',
  lineHeight: '110%',
  sx: { label: 'display48' },
  ...normalizeSpacing,
})

const variantDisplay40To48: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: { base: '-0.4px', lg: '-0.576px' },
  fontFamily: 'heading',
  fontSize: { base: '4xl', lg: '5xl' },
  fontWeight: 'extrabold',
  lineHeight: '110%',
  sx: { label: 'display40To48' },
  ...normalizeSpacing,
})

const variantDisplay32To48: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: { base: '-0.32px', md: '-0.4px', lg: '-0.576px' },
  fontFamily: 'heading',
  fontSize: { base: '3xl', md: '4xl', lg: '5xl' },
  fontWeight: { base: 'bold', md: 'extrabold' },
  lineHeight: { base: '120%', lg: '110%' },
  sx: { label: 'display32To48' },
  ...normalizeSpacing,
})

const variantDisplay32To40: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: { base: '-0.32px', lg: '-0.4px' },
  fontFamily: 'heading',
  fontSize: { base: '3xl', lg: '4xl' },
  fontWeight: { base: 'bold', lg: 'extrabold' },
  lineHeight: { base: '120%', lg: '110%' },
  sx: { label: 'display32To40' },
  ...normalizeSpacing,
})

const variantDisplay28To32: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: { base: '-0.28px', lg: '-0.32px' },
  fontFamily: 'heading',
  fontSize: { base: '2xl', lg: '3xl' },
  fontWeight: 'bold',
  lineHeight: '120%',
  sx: { label: 'display28To32' },
  ...normalizeSpacing,
})

const variantDisplay24To28: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: { base: '-0.24px', lg: '-0.28px' },
  fontFamily: 'heading',
  fontSize: { base: 'xl', lg: '3xl' },
  fontWeight: 'bold',
  lineHeight: '120%',
  sx: { label: 'display24To28' },
  ...normalizeSpacing,
})

const variantDisplay20To24: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: { base: '-0.2px', lg: '-0.24px' },
  fontFamily: 'heading',
  fontSize: { base: 'lg', lg: 'xl' },
  fontWeight: 'bold',
  lineHeight: '120%',
  sx: { label: 'display20To24' },
  ...normalizeSpacing,
})

const baseStyle: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'gray.0')(props),
  letterSpacing: '-0.24px',
  fontFamily: 'heading',
  fontWeight: 'bold',
  lineHeight: '120%',
  ...normalizeSpacing,
})

// These variants are curated by Design,
// no changes or updates should be made
// without consulting the Design team.
export const variantsHeading = {
  display48: variantDisplay48,
  display40To48: variantDisplay40To48,
  display32To48: variantDisplay32To48,
  display32To40: variantDisplay32To40,
  display28To32: variantDisplay28To32,
  display24To28: variantDisplay24To28,
  display20To24: variantDisplay20To24,
}

export type VariantsHeading = keyof typeof variantsHeading

export const Z4Heading: ComponentStyleConfig = {
  baseStyle,
  defaultProps: { size: null },
  variants: variantsHeading,
}
